<script setup lang="ts">
/**
 * Left cell for the mobile payments table.
 *
 * Layout mirrors the sketch: a narrow icon tile on the left (status icon +
 * abbreviated label stacked) and the main text block on the right (client
 * name, status chip, optional reason text). The icon tile gives instant
 * visual scanning by color; the chip text confirms the status in words.
 */
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    name: string
    paymentStatus: string
    text?: string | null
    subscription?: string | null
    width?: string
  }>(),
  {},
)

const status = computed(() => props.paymentStatus.trim().toUpperCase())

const iconConfig = computed(() => {
  switch (status.value) {
    case 'IDENTIFIED':
      return { variant: 'ok' as const, tileLabel: 'ok' }
    case 'AMBIGUOUS':
      return { variant: 'warn' as const, tileLabel: 'amb' }
    case 'UNIDENTIFIED':
      return { variant: 'warn' as const, tileLabel: '?' }
    default:
      return { variant: 'err' as const, tileLabel: '–' }
  }
})

const chipConfig = computed(() => {
  switch (status.value) {
    case 'IDENTIFIED':
      return { variant: 'ok' as const, label: 'identified' }
    case 'AMBIGUOUS':
      return { variant: 'warn' as const, label: 'ambiguous' }
    case 'UNIDENTIFIED':
      return { variant: 'warn' as const, label: 'not identified' }
    default:
      return { variant: 'err' as const, label: props.paymentStatus.toLowerCase() || 'no status' }
  }
})

const showChip = computed(() => status.value !== 'IDENTIFIED')

const showText = computed(
  () => status.value !== 'IDENTIFIED' && typeof props.text === 'string' && props.text.trim() !== '',
)
</script>

<template>
  <td class="payment-mobile-left font-tufte text-tufte-text">
    <div class="row-layout">
      <!-- icon tile -->
      <div class="row-icon" :class="`row-icon--${iconConfig.variant}`">
        <svg
          v-if="iconConfig.variant === 'ok'"
          width="22"
          height="18"
          viewBox="0 0 28 22"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M5 11.5l5.5 5.5L23 6" />
        </svg>
        <svg
          v-else-if="iconConfig.variant === 'warn'"
          width="22"
          height="18"
          viewBox="0 0 28 22"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M14 2L26 20H2L14 2z" />
          <path d="M14 9v5M14 16.5v1" />
        </svg>
        <svg
          v-else
          width="22"
          height="18"
          viewBox="0 0 28 22"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M7 5l14 12M21 5L7 17" />
        </svg>
        <span class="icon-label">{{ iconConfig.tileLabel }}</span>
      </div>

      <!-- main block -->
      <div class="row-main">
        <span class="row-name">{{ name }}</span>
        <div v-if="showChip" class="row-sub">
          <span class="status-chip" :class="`status-chip--${chipConfig.variant}`">
            {{ chipConfig.label }}
          </span>
        </div>
        <div v-if="subscription" class="row-sub">
          <span class="subscription-tag">{{ subscription }}</span>
        </div>
        <p v-if="showText" class="row-reason">{{ text }}</p>
      </div>
    </div>
  </td>
</template>

<style scoped>
.payment-mobile-left {
  vertical-align: top;
}

.row-layout {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
}

/* icon tile: fixed width, icon + tiny label stacked */
.row-icon {
  flex-shrink: 0;
  width: 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  padding-top: 0.1rem;
}

.row-icon svg {
  display: block;
}

.row-icon--ok {
  color: var(--color-money-positive);
}
.row-icon--warn {
  color: var(--color-visit-stale);
}
.row-icon--err {
  color: var(--color-money-negative);
}

.icon-label {
  font-family: var(--font-tufte-sans);
  font-size: 0.6rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
  text-align: center;
}

/* main text block */
.row-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.row-name {
  font-size: var(--text-tufte-lg);
  font-weight: 500;
  line-height: 1.2;
}

.row-sub {
  display: flex;
  align-items: center;
}

.subscription-tag {
  display: inline-block;
  background: var(--color-subscription-band);
  color: var(--color-subscription-ink);
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  padding: 0.1rem 0.42rem;
  white-space: nowrap;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.02em;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  border: 1px solid;
  line-height: 1.4;
}

.status-chip--ok {
  color: var(--color-money-positive);
  border-color: color-mix(in srgb, var(--color-money-positive) 30%, var(--color-tufte-border));
  background: color-mix(in srgb, var(--color-money-positive) 8%, var(--color-tufte-bg));
}

.status-chip--warn {
  color: var(--color-visit-stale);
  border-color: color-mix(in srgb, var(--color-visit-stale) 30%, var(--color-tufte-border));
  background: color-mix(in srgb, var(--color-visit-stale) 8%, var(--color-tufte-bg));
}

.status-chip--err {
  color: var(--color-money-negative);
  border-color: color-mix(in srgb, var(--color-money-negative) 30%, var(--color-tufte-border));
  background: color-mix(in srgb, var(--color-money-negative) 8%, var(--color-tufte-bg));
}

.row-reason {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  line-height: 1.4;
  margin: 0;
  padding-top: 0.05rem;
}
</style>
